import {useCallback, useMemo} from 'react';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync} from 'react-use';
import {FieldsService} from '../api/generated/FieldsService';
import {BasicPage} from '../components/basic-page';
import {BreadcrumbPart} from '../components/page-title';
import {ROUTES} from '../routes';
import {fieldSchema, FieldSchema} from './schemas';
import {createForm} from '../forms';
import {FieldTypes} from '../api/generated/enums';
import {SubmitHandler} from 'react-hook-form';
import {Link, useNavigate} from 'react-router-dom';
import {useNotification} from '../hooks/use-notifications';
import {Button, Group} from '@mantine/core';
import {AsyncStateContainer} from '../components/async-state-container';
import {PartnerSitesService} from '../api/generated/PartnerSitesService';
import {useOptions} from '../hooks/use-options';

const breadcrumbs: BreadcrumbPart[] = [
  {title: 'Fields', href: ROUTES.ADMIN.FIELDS.INDEX.path},
  {title: 'Create'},
];

const Form = createForm<FieldSchema>();

const DEFAULT_VALUES: FieldSchema = {
  sortOrder: 0,
  name: '',
  type: FieldTypes.Checkbox,
  inactive: false,
  partnerSiteIds: [],
};

export const FieldCreate = () => {
  const navigate = useNavigate();
  const notifications = useNotification();

  const onSubmit = useCallback<SubmitHandler<FieldSchema>>(
    async (value) => {
      const response = await FieldsService.create({body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Field Created');
      navigate(ROUTES.ADMIN.FIELDS.INDEX.path);
    },
    [navigate, notifications]
  );

  return (
    <BasicPage title={breadcrumbs}>
      <Form
        defaultValues={DEFAULT_VALUES}
        schema={fieldSchema}
        onSubmit={onSubmit}
      >
        <Form.Container>
          <FormFields />
          <Group mt={24} justify="flex-end">
            <Button
              type="button"
              variant="outline"
              component={Link}
              to={ROUTES.ADMIN.FIELDS.INDEX.path}
            >
              Cancel
            </Button>
            <Form.Submit>Create Field</Form.Submit>
          </Group>
        </Form.Container>
      </Form>
    </BasicPage>
  );
};

export const FieldUpdate = () => {
  const notifications = useNotification();
  const navigate = useNavigate();

  const {id} = useTypedParams(ROUTES.ADMIN.FIELDS.DETAIL);

  const fetchField = useAsync(async () => {
    const {data} = await FieldsService.getById({id});
    return data;
  }, [id]);

  const field = fetchField.value || undefined;
  const fieldName = field?.name ?? '';

  const breadcrumbs = useMemo<BreadcrumbPart[]>(
    () => [
      {title: 'Fields', href: ROUTES.ADMIN.FIELDS.INDEX.path},
      {title: fieldName},
    ],
    [fieldName]
  );

  const onSubmit = useCallback<SubmitHandler<FieldSchema>>(
    async (value) => {
      const response = await FieldsService.update({id, body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Field Updated');
      navigate(ROUTES.ADMIN.FIELDS.INDEX.path);
    },
    [id, navigate, notifications]
  );

  return (
    <BasicPage title={breadcrumbs}>
      <AsyncStateContainer {...fetchField}>
        <Form defaultValues={field} schema={fieldSchema} onSubmit={onSubmit}>
          <Form.Container>
            <FormFields />
            <Group mt={24} justify="flex-end">
              <Button
                type="button"
                variant="outline"
                component={Link}
                to={ROUTES.ADMIN.FIELDS.INDEX.path}
              >
                Cancel
              </Button>
              <Form.Submit>Update Field</Form.Submit>
            </Group>
          </Form.Container>
        </Form>
      </AsyncStateContainer>
    </BasicPage>
  );
};

const FormFields = () => {
  const partnerSiteOptions =
    useOptions(PartnerSitesService.getOptions).value ?? [];

  return (
    <>
      <Form.Row>
        <Form.TextInput name="name" label="Name" />
        <Form.MultiSelect
          name="partnerSiteIds"
          label="Partner Site"
          data={partnerSiteOptions}
          searchable
        />
      </Form.Row>
      <Form.Row mt={14}>
        <Group grow>
          <Form.Select name="type" label="Type" enumObject={FieldTypes} />
          <Form.NumberInput name="sortOrder" label="Sort Order" />
        </Group>
        <Form.Checkbox name="inactive" label="Inactive" />
      </Form.Row>
    </>
  );
};
